import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useFormikContext, useField } from 'formik'
import classNames from 'classnames'
import { cloneDeep, isEmpty } from 'lodash'
import DropdownTreeSelect from 'react-dropdown-tree-select'
import { IconX } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { ReactSelectChevron } from '@/components/icons/ReactSelectChevron'
import { FormField } from '@/components/forms/FormField'
import { getNestedPlaceholderText } from '@/reducers/callSearch/helpers'

import '../NestedSelect.scss'

export const NestedSelect = ({ label, name, options = [], disabled, className }) => {
  const [field, helpers] = useField(name)
  const { setFieldValue } = useFormikContext()
  const { value } = field
  const safeValue =
    typeof value === 'object' && value !== null ? value : { selected: [], includes: true }
  const includes = safeValue.includes ?? true
  const selected = safeValue.selected ?? []

  const { setValue } = helpers
  const safeSetValue = typeof setValue === 'function' ? setValue : (val) => setFieldValue(name, val)

  const [controlsVisible, setControlsVisible] = useState(false)
  const [tooltipVisible, setToolTipVisible] = useState(false)
  const [search, setSearch] = useState('')
  const dropdownContainerRef = useRef(null)

  useEffect(() => {
    if (controlsVisible && dropdownContainerRef.current) {
      const dropdownEl = dropdownContainerRef.current.querySelector('.dropdown-content')
      if (dropdownEl) {
        const rect = dropdownEl.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.top
        const maxHeight = Math.min(spaceBelow - 20, 400)
        dropdownEl.style.maxHeight = `${maxHeight}px`
        dropdownEl.style.overflowY = 'auto'
      }
    }
  }, [controlsVisible])

  const filteredOptions = useMemo(() => {
    const safeOptions = cloneDeep(options)
    safeOptions.forEach((option) => {
      option.className = 'dispositions'
      option.checked =
        Array.isArray(selected) && selected.some((entry) => entry.value === option.value)
    })
    selected.forEach((sel) => {
      if (!safeOptions.some((opt) => opt.value === sel.value)) {
        safeOptions.push({
          label: sel.value,
          value: sel.value,
          className: 'dispositions',
          checked: true,
          children: [],
        })
      }
    })

    return safeOptions.filter((option) => option.value.toLowerCase().includes(search.toLowerCase()))
  }, [options, search, selected])
  const showPill = Boolean(!controlsVisible && selected?.length)
  const cleanSelectedNodes = (nodes) => {
    return nodes.map(({ value }) => ({ value }))
  }
  const toggleIncludesClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    safeSetValue({ selected, includes: !includes })
  }

  const resetSelectedEntries = (event) => {
    event.preventDefault()
    event.stopPropagation()
    safeSetValue({ selected: [], includes })
  }

  const onChange = (selectedNode, selectedNodes) => {
    safeSetValue({ selected: cleanSelectedNodes(selectedNodes), includes })
  }

  const handleMouseTooltip = (visible) => {
    if (controlsVisible) return
    setToolTipVisible(visible)
  }
  const totalEntries = Array.isArray(selected) ? selected.length : 0
  const optionsEmpty = !Array.isArray(options) || options.length === 0

  const dropdownClasses = classNames('', {
    active: controlsVisible,
    selected: totalEntries > 0,
  })

  const chevronClasses = classNames('chevron', { active: controlsVisible })

  const isDisabled = disabled || (optionsEmpty && selected.length === 0)

  const includesClasses = classNames('occurs', { not: !includes })

  return (
    <FormField className={className} data-testid={`${name}-nested-select`}>
      {label && (
        <label htmlFor={name} className="nested-select-label">
          {label}
        </label>
      )}
      <div
        data-testid="nested-select-container"
        onMouseEnter={() => handleMouseTooltip(true)}
        onMouseLeave={() => handleMouseTooltip(false)}
        style={{ marginTop: controlsVisible ? '-41px' : null, position: 'relative' }}
      >
        {tooltipVisible && isDisabled && (
          <div className="nested-select-tooltip" data-testid={`nested-select-tooltip-${name}`}>
            <div data-testid={`nested-select-tooltip-content-${name}`}>
              No dispositions available for this organization
            </div>
            <div className="nested-select-tail-1" />
            <div className="nested-select-tail-2" />
          </div>
        )}
        <ReactSelectChevron
          onClick={() => {
            if (!isDisabled) setControlsVisible(!controlsVisible)
          }}
          data-testid="nested-select-chevron"
          className={chevronClasses}
        />
        {controlsVisible && (
          <>
            <div className="nested-select-overlay" onClick={() => setControlsVisible(false)} />
            <div data-testid="nested-select-controls" className="controls">
              <Button
                xs
                data-testid={`nested-select-controls-occurs-${name}`}
                onClick={toggleIncludesClick}
                className={includesClasses}
              >
                {includes ? 'Include' : 'Exclude'}
              </Button>
              <div className="selection-container">
                <span data-testid="nested-select-controls-selected-count">
                  {`${totalEntries} selected`}
                </span>
                <a data-testid="nested-select-controls-clear" onClick={resetSelectedEntries}>
                  Clear
                </a>
              </div>
            </div>
          </>
        )}
        {showPill && (
          <div className="placeholder">
            <div data-testid="nested-select-pill" className="nested-select-pill">
              {`${totalEntries} selected`}
            </div>
            <IconX
              onClick={resetSelectedEntries}
              className="nested-select-clear-x"
              data-testid="nested-select-clear-x"
            />
          </div>
        )}
        {isEmpty(filteredOptions) && controlsVisible && (
          <div className="nested-select-no-options" data-testid="nested-select-no-options">
            No Options
          </div>
        )}
        <input
          className="nested-select-input"
          onFocus={() => setControlsVisible(true)}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
          placeholder={getNestedPlaceholderText(isDisabled, controlsVisible, showPill)}
          disabled={isDisabled}
        />
        {controlsVisible && (
          <div ref={dropdownContainerRef}>
            <DropdownTreeSelect
              data-testid="nested-select"
              className={dropdownClasses}
              data={filteredOptions}
              showDropdown="initial"
              disabled={isDisabled}
              onChange={onChange}
              onFocus={() => setControlsVisible(true)}
              onBlur={() => setControlsVisible(false)}
            />
          </div>
        )}
      </div>
    </FormField>
  )
}
